import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STRIPE_PAYMENT_LINK =
  "https://buy.stripe.com/bJeaEXfdo3EDd1C9i83Nm01";

const THEME_ALIASES = {
  mexican: "tacos",
  vegan: "tacos",
  breakfast: "cafe",
  dessert: "icecream",
  mediterranean: "fine_dining",
  thai: "sushi",
  indian: "cajun",
  diner: "default",
  bar: "bar",
};

const normalizeTheme = (theme) => {
  if (!theme) return "default";
  return THEME_ALIASES[theme] || theme;
};

const THEMES = {
  default: {
    accent: "#00E5FF",
    button: "#00E5FF",
    buttonHover: "#66FFFF",
    border: "rgba(0,229,255,0.35)",
    bgFrom: "#09090B",
    bgTo: "#121214",
  },

  pizza: {
    accent: "#E63946",
    button: "#E63946",
    buttonHover: "#F15B67",
    border: "rgba(230,57,70,0.35)",
  },

  burger: {
    accent: "#F4B400",
    button: "#F4B400",
    buttonHover: "#FFD54F",
    border: "rgba(244,180,0,0.35)",
  },

  tacos: {
    accent: "#2E7D32",
    button: "#2E7D32",
    buttonHover: "#43A047",
    border: "rgba(46,125,50,0.35)",
  },

  cafe: {
    accent: "#6F4E37",
    button: "#6F4E37",
    buttonHover: "#8D6E63",
    border: "rgba(111,78,55,0.35)",
  },

  italian: {
    accent: "#C62828",
    button: "#C62828",
    buttonHover: "#E53935",
    border: "rgba(198,40,40,0.35)",
  },

  fine_dining: {
    accent: "#D4AF37",
    button: "#D4AF37",
    buttonHover: "#E5C85B",
    border: "rgba(212,175,55,0.35)",
  },

  bbq: {
    accent: "#B3541E",
    button: "#B3541E",
    buttonHover: "#C96A2C",
    border: "rgba(179,84,30,0.35)",
  },

  seafood: {
    accent: "#0288D1",
    button: "#0288D1",
    buttonHover: "#29B6F6",
    border: "rgba(2,136,209,0.35)",
  },

  cajun: {
    accent: "#D84315",
    button: "#D84315",
    buttonHover: "#F4511E",
    border: "rgba(216,67,21,0.35)",
  },

  chicken: {
    accent: "#E6A700",
    button: "#E6A700",
    buttonHover: "#FBC02D",
    border: "rgba(230,167,0,0.35)",
  },

  chinese: {
    accent: "#B71C1C",
    button: "#B71C1C",
    buttonHover: "#D32F2F",
    border: "rgba(183,28,28,0.35)",
  },

  sushi: {
    accent: "#5E35B1",
    button: "#5E35B1",
    buttonHover: "#7E57C2",
    border: "rgba(94,53,177,0.35)",
  },

  bakery: {
    accent: "#C68642",
    button: "#C68642",
    buttonHover: "#D9A066",
    border: "rgba(198,134,66,0.35)",
  },

  icecream: {
    accent: "#EC4899",
    button: "#EC4899",
    buttonHover: "#F472B6",
    border: "rgba(236,72,153,0.35)",
  },

  bar: {
    accent: "#D946EF",
    button: "#D946EF",
    buttonHover: "#F0ABFC",
    border: "rgba(217,70,239,0.35)",
  },

  hotdog: {
    accent: "#C62828",
    button: "#C62828",
    buttonHover: "#E53935",
    border: "rgba(198,40,40,0.35)",
  },
};

function themeFor(data) {
  const key = normalizeTheme(
    (data?.theme || "default").toLowerCase()
  );

  return THEMES[key] || THEMES.default;
}

function offerLabelFor(data) {
  const key = normalizeTheme(
    (data?.theme || "default").toLowerCase()
  );

  const labels = {
    default: "This Week's Offer",
    pizza: "Today's Pizza Special",
    burger: "House Burger Special",
    tacos: "Fiesta Special",
    cafe: "Café Special",
    italian: "Chef's Italian Special",
    fine_dining: "Exclusive Chef's Offer",
    bbq: "Pitmaster's Special",
    seafood: "Fresh Catch",
    cajun: "House Special",
    chicken: "Chicken Special",
    chinese: "House Favorite",
    sushi: "Chef's Special",
    bakery: "Fresh From the Oven",
    icecream: "Sweet Treat",
    bar: "Tonight's Special",
    hotdog: "Grill Special",
  };

  return labels[key] || labels.default;
}

function restaurantIdentityFor(data = {}) {
  const rating = Number(data.rating || 0);
  const reviewCount = Number(
    data.rating_count || data.ratingCount || 0
  );

  if (rating >= 4.3 && reviewCount >= 25) {
    return `★ ${rating.toFixed(1)} • ${reviewCount} Reviews`;
  }

  const city = (data.city || "").toUpperCase();

  const cuisine = (data.cuisine || "")
    .replace(/restaurant/gi, "")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();

  if (city && cuisine) {
    return `${city} • ${cuisine}`;
  }

  if (city) {
    return city;
  }

  if (cuisine) {
    return cuisine;
  }

  return "LOCAL FAVORITE";
}

function titleCaseWords(value = "") {
  return String(value)
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getClientId() {
  try {
    const key = "ttt_mockup_cid";
    let value = localStorage.getItem(key);

    if (!value) {
      value =
        `c_${Math.random().toString(36).slice(2)}_` +
        Date.now().toString(36);

      localStorage.setItem(key, value);
    }

    return value;
  } catch {
    return "anon";
  }
}

function makeClaimCode(data) {
  const raw =
    data?.restaurant_name ||
    data?.instagram_handle ||
    "TTT";

  const prefix = raw
    .replace(/[^a-zA-Z]/g, "")
    .slice(0, 3)
    .toUpperCase()
    .padEnd(3, "X");

  const number = Math.floor(1000 + Math.random() * 9000);

  return `${prefix}-${number}`;
}

function smartCtasFor(data = {}) {
  const theme = normalizeTheme(
    (data.theme || "default").toLowerCase()
  );

  const phoneHref = data.phone
    ? `tel:${data.phone.replace(/[^0-9+]/g, "")}`
    : "";

  const directionsHref = data.address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        data.address
      )}`
    : "";

  const primaryLabel =
    {
      pizza: "Order Online",
      burger: "Order Online",
      tacos: "Order Online",
      mexican: "Order Online",
      vegan: "View Menu",
      cafe: "Order Ahead",
      bakery: "Order Ahead",
      icecream: "View Flavors",
      bar: "View Menu",
      fine_dining: "Reserve Table",
      seafood: "Reserve Table",
      bbq: "Order BBQ",
      cajun: "Order Online",
      chicken: "Order Online",
      chinese: "Order Online",
      sushi: "Order Online",
      hotdog: "Order Online",
    }[theme] || "Reserve Table";

  return [
    data.cta_link && {
      label: primaryLabel,
      href: data.cta_link,
      action: "cta_link_click",
    },

    data.menu_url && {
      label: "View Menu",
      href: data.menu_url,
      action: "menu_click",
    },

    phoneHref && {
      label: "Call",
      href: phoneHref,
      action: "call_click",
    },

    directionsHref && {
      label: "Directions",
      href: directionsHref,
      action: "directions_click",
    },

    data.website_url && {
      label: "Website",
      href: data.website_url,
      action: "website_click",
    },
  ]
    .filter(Boolean)
    .slice(0, 4);
}

function afterClaimCtasFor(data = {}) {
  const base = smartCtasFor(data);

  const customText = String(
    data.after_claim_button_text || ""
  ).trim();

  const customLink = String(
    data.after_claim_button_link || ""
  ).trim();

  const custom =
    customText && customLink
      ? {
          label: customText,
          href: customLink,
          action: "after_claim_action_click",
        }
      : null;

  const combined = [
    custom,
    ...base.filter(
      (cta) =>
        !custom ||
        cta.href !== custom.href ||
        cta.label !== custom.label
    ),
  ].filter(Boolean);

  return combined.slice(0, 5);
}

function eventCtasFor(data = {}) {
  const phoneHref = data.phone
    ? `tel:${data.phone.replace(/[^0-9+]/g, "")}`
    : "";

  const directionsHref = data.address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        data.address
      )}`
    : "";

  return [
    {
      label: data.event_action_text || "View Event Details",
      href: data.event_action_link || "",
      action: "event_action_click",
      primary: true,
    },

    data.website_url && {
      label: "Website",
      href: data.website_url,
      action: "website_click",
    },

    phoneHref && {
      label: "Call",
      href: phoneHref,
      action: "call_click",
    },

    directionsHref && {
      label: "Directions",
      href: directionsHref,
      action: "directions_click",
    },
  ]
    .filter(Boolean)
    .slice(0, 4);
}

function claimTextFor(data = {}) {
  if (
    data.cta_text &&
    data.cta_text !== "Claim My Deal"
  ) {
    return data.cta_text;
  }

  const theme = normalizeTheme(
    (data.theme || "default").toLowerCase()
  );

  const labels = {
    pizza: "Claim Pizza Deal",
    burger: "Claim Burger Special",
    tacos: "Claim Fiesta Special",
    mexican: "Claim Taco Deal",
    vegan: "Claim Fresh Deal",
    cafe: "Claim Coffee Reward",
    bakery: "Claim Bakery Treat",
    icecream: "Claim Sweet Treat",
    bar: "Claim Tonight's Deal",
    fine_dining: "Unlock Chef's Offer",
    seafood: "Claim Fresh Catch",
    bbq: "Claim BBQ Special",
    cajun: "Claim Cajun Special",
    chicken: "Claim Chicken Deal",
    chinese: "Claim Chinese Special",
    sushi: "Claim Chef's Special",
    hotdog: "Claim Hot Dog Special",
  };

  return labels[theme] || "Claim My Deal";
}

export default function MockupPreview() {
  const { slug } = useParams();

  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [claimed, setClaimed] = useState(false);
  const [claimedAt, setClaimedAt] = useState("");
  const [claimCode, setClaimCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadPreview = async () => {
      try {
        const resetClaim =
          new URLSearchParams(window.location.search).get(
            "reset"
          ) === "1";

        if (resetClaim) {
          try {
            localStorage.removeItem(
              `ttt_mockup_claimed_${slug}`
            );

            localStorage.removeItem(
              `ttt_mockup_claim_code_${slug}`
            );
          } catch {
            /* noop */
          }

          setClaimed(false);
          setClaimedAt("");
          setClaimCode("");
        }

        const response = await fetch(
          `${API}/mockups/by-slug/${slug}`
        );

        if (response.status === 404) {
          if (!cancelled) {
            setState("notfound");
          }

          return;
        }

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const body = await response.json();

        if (!cancelled) {
          setData(body);
          setState("ok");
        }
      } catch (error) {
        console.error(
          "Failed to load TapToTable Preview:",
          error
        );

        if (!cancelled) {
          setState("error");
        }
      }
    };

    loadPreview();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    const resetClaim =
      new URLSearchParams(window.location.search).get(
        "reset"
      ) === "1";

    if (resetClaim) {
      return;
    }

    try {
      const savedAt = localStorage.getItem(
        `ttt_mockup_claimed_${slug}`
      );

      const savedCode = localStorage.getItem(
        `ttt_mockup_claim_code_${slug}`
      );

      if (savedAt) {
        setClaimed(true);
        setClaimedAt(savedAt);
      }

      if (savedCode) {
        setClaimCode(savedCode);
      }
    } catch {
      /* noop */
    }
  }, [slug]);

  const trackEvent = async (
    mockupId,
    type,
    action = "",
    extra = {}
  ) => {
    try {
      await fetch(`${API}/mockups/${mockupId}/${type}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          client_id: getClientId(),
          action,
          ...extra,
        }),
      });
    } catch (error) {
      console.warn("Tracking failed:", error);
    }
  };

  const onCtaClick = async (event) => {
    event.preventDefault();

    if (
      submitting ||
      claimed ||
      !data?.id
    ) {
      return;
    }

    setSubmitting(true);

    try {
      const timestamp = new Date().toISOString();
      const code = makeClaimCode(data);

      await Promise.all([
        trackEvent(
          data.id,
          "click",
          "cta_click"
        ),

        trackEvent(
          data.id,
          "claim",
          "offer_claim",
          {
            claim_code: code,
          }
        ),
      ]);

      try {
        localStorage.setItem(
          `ttt_mockup_claimed_${slug}`,
          timestamp
        );

        localStorage.setItem(
          `ttt_mockup_claim_code_${slug}`,
          code
        );
      } catch {
        /* noop */
      }

      setClaimedAt(timestamp);
      setClaimCode(code);
      setClaimed(true);
    } finally {
      setSubmitting(false);
    }
  };

  const onSecondaryClick = (action) => {
    if (data?.id) {
      trackEvent(
        data.id,
        "click",
        action,
        {
          claim_code: claimCode || "",
        }
      );
    }
  };

  const resetOfferView = () => {
    try {
      localStorage.removeItem(
        `ttt_mockup_claimed_${slug}`
      );

      localStorage.removeItem(
        `ttt_mockup_claim_code_${slug}`
      );
    } catch {
      /* noop */
    }

    setClaimed(false);
    setClaimedAt("");
    setClaimCode("");
  };

  const claimedTimeStr = useMemo(() => {
    if (!claimedAt) {
      return "";
    }

    try {
      return new Date(claimedAt).toLocaleString();
    } catch {
      return claimedAt;
    }
  }, [claimedAt]);

  if (state === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#09090B]">
        <div className="h-7 w-7 animate-spin rounded-full border-2 border-[#00E5FF] border-t-transparent" />
      </div>
    );
  }

  if (state === "notfound") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#09090B] px-6 text-center text-white">
        <div className="font-display text-2xl font-semibold">
          Preview not found
        </div>

        <p className="mt-2 text-sm text-zinc-400">
          This TapToTable Preview link is no longer active.
        </p>

        <Link
          to="/"
          className="mt-6 inline-flex rounded-full bg-[#00E5FF] px-5 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-[#66FFFF]"
        >
          Visit TapToTable
        </Link>
      </div>
    );
  }

  if (
    state === "error" ||
    !data
  ) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#09090B] px-6 text-center text-white">
        <div className="font-display text-xl font-semibold">
          Something went wrong
        </div>

        <p className="mt-2 text-sm text-zinc-400">
          Please refresh the page and try again.
        </p>
      </div>
    );
  }

  const theme = themeFor(data);
  const savedBrandColor = String(data.brand_color || "").trim();
  const brand = /^#[0-9A-Fa-f]{6}$/.test(savedBrandColor)
    ? savedBrandColor
    : theme.accent;

  const cityLine = [
    data.city,
    data.cuisine ? titleCaseWords(data.cuisine) : "",
  ]
    .filter(Boolean)
    .join(" · ");

  const hasCta = Boolean(data.cta_text);
  const restaurantIdentity =
    restaurantIdentityFor(data);

  const offerLabel = offerLabelFor(data);
  const claimText = claimTextFor(data);
  const smartCtas = smartCtasFor(data);
  const afterClaimCtas = afterClaimCtasFor(data);
  const isEvent = (data.campaign_type || "offer") === "event";
  
  const isFeaturedItem =
    data.campaign_type === "featured_item";

  const eventCtas = eventCtasFor(data);

  const eventDateLabel = data.event_date
    ? new Date(`${data.event_date}T12:00:00`).toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "";

  const activationUrl =
    data.payment_link || STRIPE_PAYMENT_LINK;

  return (
    <div className="flex min-h-screen flex-col bg-[#09090B] font-body text-white">
      <main className="flex flex-1 flex-col items-center px-5 pb-14 pt-8">
        <div className="w-full max-w-md">
          <div className="relative aspect-[4/3] w-full overflow-hidden rounded-3xl border border-white/10 shadow-2xl">
            {data.hero_image_url ? (
              <img
                src={data.hero_image_url}
                alt={data.restaurant_name}
                className="absolute inset-0 h-full w-full object-cover"
                onError={(event) => {
                  event.currentTarget.style.display =
                    "none";
                }}
              />
            ) : (
              <div
                className="absolute inset-0"
                style={{
                  background:
                    `linear-gradient(135deg, ` +
                    `${brand}33 0%, #09090B 70%)`,
                }}
              />
            )}

            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/15 to-transparent" />

            <div className="absolute bottom-4 left-5 right-5">
              <div
                className="text-[10px] font-bold uppercase tracking-[0.2em]"
                style={{
                  color: brand,
                }}
              >
                {restaurantIdentity}
              </div>

              <h1 className="mt-1 font-display text-2xl font-semibold leading-tight sm:text-3xl">
                {data.restaurant_name}
              </h1>

              {data.instagram_handle && (
                <div className="mt-1 text-xs text-zinc-300">
                  @
                  {String(
                    data.instagram_handle
                  ).replace(/^@/, "")}
                </div>
              )}
            </div>
          </div>

          {cityLine && (
            <div className="mt-5 text-center text-sm text-zinc-400">
              {cityLine}
            </div>
          )}

          {isEvent ? (
            <>
              <div
                className="mt-6 rounded-2xl border p-5"
                style={{
                  borderColor: `${brand}55`,
                  background: `${brand}10`,
                }}
              >
                <div
                  className="text-[10px] font-bold uppercase tracking-[0.2em]"
                  style={{ color: brand }}
                >
                  Upcoming Event
                </div>

                <div className="mt-2 font-display text-xl font-semibold leading-snug">
                  {data.event_name || "Restaurant Event"}
                </div>

                {(eventDateLabel || data.event_time) && (
                  <div className="mt-3 flex flex-wrap gap-2 text-xs text-zinc-300">
                    {eventDateLabel ? (
                      <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5">
                        {eventDateLabel}
                      </span>
                    ) : null}

                    {data.event_time ? (
                      <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5">
                        {data.event_time}
                      </span>
                    ) : null}
                  </div>
                )}

                {data.event_description ? (
                  <p className="mt-4 text-sm leading-relaxed text-zinc-300">
                    {data.event_description}
                  </p>
                ) : null}
              </div>

              {eventCtas.length > 0 ? (
                <div className="mt-5 space-y-3">
                  {eventCtas[0] ? (
                    eventCtas[0].href ? (
                      <a
                        href={eventCtas[0].href}
                        target={
                          eventCtas[0].href.startsWith("http")
                            ? "_blank"
                            : undefined
                        }
                        rel={
                          eventCtas[0].href.startsWith("http")
                            ? "noreferrer"
                            : undefined
                        }
                        onClick={() =>
                          onSecondaryClick(eventCtas[0].action)
                        }
                        className="inline-flex min-h-[52px] w-full items-center justify-center rounded-2xl px-4 py-3 text-center text-sm font-semibold transition-opacity hover:opacity-90"
                        style={{
                          background: brand,
                          color: "#000",
                        }}
                      >
                        {eventCtas[0].label}
                      </a>
                    ) : (
                      <div
                        className="inline-flex min-h-[52px] w-full items-center justify-center rounded-2xl px-4 py-3 text-center text-sm font-semibold opacity-70"
                        style={{
                          background: brand,
                          color: "#000",
                        }}
                        title="Add a destination link in the editor to make this button clickable."
                      >
                        {eventCtas[0].label}
                      </div>
                    )
                  ) : null}

                  {eventCtas.slice(1).length > 0 ? (
                    <div className="grid grid-cols-3 gap-3">
                      {eventCtas.slice(1).map((cta) => (
                        <a
                          key={`${cta.action}-${cta.label}`}
                          href={cta.href}
                          target={
                            cta.href.startsWith("http")
                              ? "_blank"
                              : undefined
                          }
                          rel={
                            cta.href.startsWith("http")
                              ? "noreferrer"
                              : undefined
                          }
                          onClick={() => onSecondaryClick(cta.action)}
                          className="inline-flex min-h-[48px] items-center justify-center rounded-xl border border-white/10 bg-white/5 px-2 py-2.5 text-center text-xs transition-colors hover:bg-white/10"
                        >
                          {cta.label}
                        </a>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </>

          ) : isFeaturedItem ? (
            <>
              <div
                className="mt-6 rounded-2xl border p-5"
                style={{
                  borderColor: `${brand}55`,
                  background: `${brand}10`,
                }}
              >
                <div
                  className="text-[10px] font-bold uppercase tracking-[0.2em]"
                  style={{ color: brand }}
                >
                  Featured Menu Item
                </div>

                <h2 className="mt-2 font-display text-xl font-semibold leading-snug">
                  {data.featured_item_name || "Featured Item"}
                </h2>

                {data.featured_item_description && (
                  <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-zinc-300">
                    {data.featured_item_description}
                  </p>
                )}
              </div>

              {(data.featured_action_link || data.menu_url) ? (
                <a
                  href={data.featured_action_link || data.menu_url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-5 inline-flex min-h-[52px] w-full items-center justify-center rounded-2xl px-4 py-3 text-center text-sm font-semibold hover:opacity-90"
                  style={{ background: brand, color: "#000" }}
                >
                  {data.featured_action_text || "View Menu"}
                </a>
              ) : (
                <div className="mt-5 rounded-xl border border-white/10 bg-white/5 p-4 text-center text-sm text-zinc-400">
                  Visit us to discover this featured item.
                </div>
              )}
            </>
          ) : (

            <>
              {!claimed && (
                <>
                  {data.offer && (
                    <div
                      className="mt-6 rounded-2xl border p-5"
                      style={{
                        borderColor: `${brand}55`,
                        background: `${brand}10`,
                      }}
                    >
                      <div
                        className="text-[10px] font-bold uppercase tracking-[0.2em]"
                        style={{
                          color: brand,
                        }}
                      >
                        {offerLabel}
                      </div>

                      <div className="mt-2 font-display text-lg font-semibold leading-snug">
                        {data.offer}
                      </div>

                      {data.description && (
                        <p className="mt-2 text-sm leading-relaxed text-zinc-400">
                          {data.description}
                        </p>
                      )}
                    </div>
                  )}

                  {hasCta && (
                    <button
                      type="button"
                      onClick={onCtaClick}
                      disabled={submitting}
                      className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl py-4 text-base font-semibold transition-colors disabled:opacity-60"
                      style={{
                        background: brand,
                        color: "#000",
                      }}
                    >
                      {submitting
                        ? "Claiming…"
                        : claimText}
                    </button>
                  )}
                </>
              )}

              {claimed && (
                <div
                  className="mt-6 rounded-2xl border p-6"
                  style={{
                    borderColor: `${brand}66`,
                    background: `${brand}10`,
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="flex h-10 w-10 items-center justify-center rounded-full font-bold text-black"
                      style={{
                        background: brand,
                      }}
                    >
                      ✓
                    </div>

                    <div>
                      <div
                        className="text-[10px] font-bold uppercase tracking-[0.2em]"
                        style={{
                          color: brand,
                        }}
                      >
                        Offer Claimed
                      </div>

                      <div className="mt-0.5 font-display text-lg font-semibold leading-tight">
                        {data.restaurant_name}
                      </div>
                    </div>
                  </div>

                  {data.offer && (
                    <div className="mt-4 text-sm font-medium leading-snug text-zinc-200">
                      {data.offer}
                    </div>
                  )}

                  {claimCode && (
                    <div
                      className="mt-4 rounded-xl border px-4 py-3 text-center"
                      style={{
                        borderColor: `${brand}66`,
                        background: `${brand}18`,
                      }}
                    >
                      <div
                        className="text-[10px] font-bold uppercase tracking-[0.2em]"
                        style={{
                          color: brand,
                        }}
                      >
                        Offer Code
                      </div>

                      <div className="mt-1 font-display text-2xl font-bold tracking-widest text-white">
                        {claimCode}
                      </div>
                    </div>
                  )}

                  <div className="mt-3 text-xs text-zinc-500">
                    {claimedTimeStr
                      ? `Claimed ${claimedTimeStr}`
                      : ""}
                  </div>

                  <div className="mt-3 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs text-zinc-300">
                    Show this code to your server to claim your
                    offer.
                  </div>

                  <div className="mt-2 text-center text-xs text-zinc-500">
                    Keep this page open or take a screenshot before visiting.
                  </div>

                  {afterClaimCtas.length > 0 && (
                    <div className="mt-5">
                      <div className="mb-2 text-left text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
                        Ready for the next step?
                      </div>

                      <a
                        href={afterClaimCtas[0].href}
                        target={
                          afterClaimCtas[0].href.startsWith("http")
                            ? "_blank"
                            : undefined
                        }
                        rel={
                          afterClaimCtas[0].href.startsWith("http")
                            ? "noreferrer"
                            : undefined
                        }
                        onClick={() =>
                          onSecondaryClick(
                            afterClaimCtas[0].action
                          )
                        }
                        className="inline-flex min-h-[56px] w-full items-center justify-center rounded-2xl px-5 py-4 text-center text-base font-bold text-black transition-all active:scale-[0.99]"
                        style={{
                          background: brand,
                          boxShadow: `0 12px 28px ${brand}33`,
                        }}
                        data-testid="mockup-after-claim-primary-action"
                      >
                        {afterClaimCtas[0].label}
                      </a>

                      {afterClaimCtas.length > 1 && (
                        <div
                          className={`mt-3 grid gap-2 ${
                            afterClaimCtas.slice(1).length === 1
                              ? "grid-cols-1"
                              : afterClaimCtas.slice(1).length === 3
                                ? "grid-cols-3"
                                : "grid-cols-2"
                          }`}
                        >
                          {afterClaimCtas.slice(1).map((cta) => (
                            <a
                              key={cta.action}
                              href={cta.href}
                              target={
                                cta.href.startsWith("http")
                                  ? "_blank"
                                  : undefined
                              }
                              rel={
                                cta.href.startsWith("http")
                                  ? "noreferrer"
                                  : undefined
                              }
                              onClick={() =>
                                onSecondaryClick(
                                  cta.action
                                )
                              }
                              className="rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-center text-xs transition-colors hover:bg-white/10"
                            >
                              {cta.label}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={resetOfferView}
                    className="mt-5 inline-flex w-full items-center justify-center rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm font-semibold text-zinc-300 transition-colors hover:bg-white/10 hover:text-white"
                  >
                    ↻ Reset to Offer Page
                  </button>
                </div>
              )}

              {!claimed && smartCtas.length > 0 && (
                <div
                  className={`mt-3 grid gap-3 ${
                    smartCtas.length === 1
                      ? "grid-cols-1"
                      : "grid-cols-2"
                  }`}
                >
                  {smartCtas.map((cta, index) => (
                    <a
                      key={cta.action}
                      href={cta.href}
                      target={
                        cta.href.startsWith("http")
                          ? "_blank"
                          : undefined
                      }
                      rel={
                        cta.href.startsWith("http")
                          ? "noreferrer"
                          : undefined
                      }
                      onClick={() => onSecondaryClick(cta.action)}
                      className={`rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-center text-sm transition-colors hover:bg-white/10 ${
                        smartCtas.length === 3 && index === 2
                          ? "col-span-2"
                          : ""
                      }`}
                    >
                      {cta.label}
                    </a>
                  ))}
                </div>
              )}
            </>
          )}

          {(data.address || data.phone) && (
            <div className="mt-5 space-y-1 rounded-xl border border-white/10 bg-[#121214] p-4 text-sm text-zinc-300">
              {data.address && (
                <div>
                  {data.address}
                </div>
              )}

              {data.phone && (
                <a
                  href={`tel:${data.phone.replace(
                    /[^0-9+]/g,
                    ""
                  )}`}
                  onClick={() =>
                    onSecondaryClick(
                      "call_click"
                    )
                  }
                  className="block text-zinc-400 transition-colors hover:text-white"
                >
                  {data.phone}
                </a>
              )}
            </div>
          )}

          <section
            className="mt-8 overflow-hidden rounded-2xl border"
            style={{
              borderColor: `${brand}66`,
              background: `${brand}0D`,
            }}
            data-testid="restaurant-owner-activation"
          >
            <div className="p-5">
              <div
                className="text-[10px] font-bold uppercase tracking-[0.2em]"
                style={{
                  color: brand,
                }}
              >
                Your Restaurant Page Is Already Built
              </div>

              <h2 className="mt-2 font-display text-xl font-semibold leading-snug text-white">
                This TapToTable Page Was Created for {data.restaurant_name}
              </h2>

              <p className="mt-3 text-sm leading-relaxed text-zinc-300">
                You do not need to start over or build another page on the
                TapToTable website. If you want this page live for customers,
                activate it for{" "}
                <span className="font-semibold text-white">$59 per month</span>.
              </p>

              <div className="mt-4 rounded-xl border border-white/10 bg-black/20 p-4">
                <div className="text-xs font-bold uppercase tracking-[0.16em] text-zinc-400">
                  Everything Included for $59/Month
                </div>

                <div className="mt-3 space-y-2 text-sm leading-relaxed text-zinc-300">
                  {[
                    "Your hosted TapToTable Page",
                    "Secure, Easy-to-Use TapToTable Dashboard",
                    "Create and update Promotions, Events, and Featured Items anytime",
                    "Update restaurant details, photos, links, and customer actions",
                    "Customer activity and supported analytics",
                    "Restaurant photo hosting + TapToTable hosting",
                    "Ongoing support",
                    "Cancel anytime",
                  ].map((item) => (
                    <div key={item} className="flex items-start gap-2">
                      <span
                        className="mt-0.5 font-bold"
                        style={{ color: brand }}
                      >
                        ✓
                      </span>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <a
                href={activationUrl}
                target="_blank"
                rel="noreferrer"
                onClick={() =>
                  onSecondaryClick(
                    "payment_click"
                  )
                }
                className="mt-5 inline-flex w-full items-center justify-center rounded-xl px-4 py-3.5 text-center text-sm font-bold transition-opacity hover:opacity-90"
                style={{
                  background: brand,
                  color: "#000",
                }}
                data-testid="activate-this-page-button"
              >
                Activate My TapToTable Page — $59/month
              </a>

              <p className="mt-3 text-center text-xs text-zinc-500">
                Secure monthly checkout through Stripe · Cancel anytime
              </p>
              <p className="mt-3 text-center text-xs leading-relaxed text-zinc-400">
                First restaurant location: $59/month. Each additional eligible
                location under the same ownership: $39/month, arranged separately.
                Each location has its own page, login, and dashboard.
              </p>
              <div className="mt-5 rounded-xl border border-white/10 bg-white/[0.035] p-4">
                <div className="text-sm font-semibold text-white">
                  Want a change before you activate?
                </div>

                <p className="mt-2 text-xs leading-relaxed text-zinc-400">
                  Reply to the message we sent you and tell us what you would
                  like adjusted. We can update your page before you activate it.
                </p>
              </div>

              <Link
                to={`/preview/${slug}`}
                className="mt-4 inline-flex w-full items-center justify-center rounded-xl border-2 px-4 py-3.5 text-center text-sm font-bold transition-all hover:-translate-y-0.5 hover:bg-white/5"
                style={{
                  borderColor: `${brand}99`,
                  color: brand,
                  boxShadow: `0 0 18px ${brand}18`,
                }}
              >
                ← Return to My Review Page
              </Link>
            </div>
          </section>
        </div>
      </main>

      <footer className="py-6 text-center text-xs text-zinc-500">
        Powered by{" "}
        <Link
          to="/"
          className="text-zinc-300 transition-colors hover:text-[#00E5FF]"
        >
          TapToTable
        </Link>
      </footer>
    </div>
  );
}
